import { useMemo, useState } from 'react'
import { Virtuoso } from 'react-virtuoso'
import { InputText } from '@/components/commons/input-text'
import { SearchLgIcon } from '@/components/ui/icons'
import { InputHelpText } from '@/components/ui/input-help-text'
import { Skeleton } from '@/components/ui/skeleton'
import type { ColorPair } from '@/features/subgeo/utils/color-utils'
import { debounce } from '@/helpers/utils'
import { useTranslation } from '@/translations/i18n'
import type { ViewOnlySubGeo } from '../types'

interface SubgeoListItemProps {
	subgeo: ViewOnlySubGeo
	color: string
	isActive: boolean
	onClick: () => void
}

const SubgeoListItem = ({
	subgeo,
	color,
	isActive,
	onClick
}: SubgeoListItemProps) => (
	<button
		className={`flex w-full cursor-pointer flex-row items-center gap-2 rounded-lg p-1.5 transition-colors ${
			isActive
				? 'bg-neutral-100 hover:bg-neutral-100'
				: 'bg-transparent hover:bg-neutral-50'
		}`}
		onClick={onClick}
		type='button'
	>
		<div
			className='max-h-[54px] w-1.5 self-stretch rounded'
			style={{ backgroundColor: color }}
		/>
		<div className='flex w-full flex-col text-left font-medium text-neutral-900 text-xs'>
			<span className='line-clamp-2 max-w-[280px] overflow-hidden text-ellipsis whitespace-break-spaces'>
				{subgeo.name}
			</span>
		</div>
	</button>
)

export interface SubgeoListProps {
	subgeos: ViewOnlySubGeo[]
	getColor: (id: string) => ColorPair
	isLoading?: boolean
	highlightedSubgeoId: string | null
	onSubgeoClick: (id: string | null) => void
}

export const SubgeoList = ({
	subgeos,
	getColor,
	isLoading = false,
	highlightedSubgeoId,
	onSubgeoClick
}: SubgeoListProps) => {
	const { t } = useTranslation()
	const [search, setSearch] = useState('')

	const onSearch = debounce((value: string) => {
		setSearch(value)
	}, 350)

	const filteredSubgeos = useMemo(() => {
		if (!search) {
			return subgeos
		}
		const searchLower = search.toLowerCase()
		return subgeos.filter((s) => s.name.toLowerCase().includes(searchLower))
	}, [subgeos, search])

	const handleSubgeoClick = (id: string) => {
		if (highlightedSubgeoId === id) {
			onSubgeoClick(null)
		} else {
			onSubgeoClick(id)
		}
	}

	return (
		<div className='flex h-[calc(100vh-180px)] w-full flex-col gap-2 rounded-2xl bg-neutral-white shadow-lg'>
			<div className='flex w-full flex-col items-start gap-2 px-2.5 pt-2.5'>
				<h6 className='font-medium text-neutral-500 text-sm'>
					{isLoading ? (
						<div className='flex items-center justify-between gap-2'>
							<Skeleton height={20} width={20} />
							<Skeleton height={20} width={80} />
						</div>
					) : (
						t('subgeo:subgeos_count', { count: subgeos.length })
					)}
				</h6>
				<InputText
					inputSize='sm'
					onChange={(e) => onSearch(e.target.value)}
					placeholder={t('subgeo:search_subgeos')}
					type='text'
					typeIcon={<SearchLgIcon size={16} />}
				/>
				{!isLoading && filteredSubgeos.length === 0 && (
					<InputHelpText helpText={t('subgeo:no_subgeos')} />
				)}
			</div>

			{isLoading ? (
				<div className='h-full w-full overflow-hidden px-2.5 pb-2.5'>
					<div className='flex flex-col gap-1'>
						{Array.from(
							{ length: 8 },
							(_, i) => `skeleton-${i}`
						).map((key) => (
							<Skeleton height={40} key={key} />
						))}
					</div>
				</div>
			) : (
				<div className='h-full w-full overflow-hidden px-2.5 pb-2.5'>
					<Virtuoso
						className='[&::-webkit-scrollbar-thumb:hover]:bg-neutral-400 [&::-webkit-scrollbar-thumb]:rounded-sm [&::-webkit-scrollbar-thumb]:bg-neutral-300 [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar]:bg-neutral-50'
						data={filteredSubgeos}
						itemContent={(_, subgeo) => (
							<SubgeoListItem
								color={getColor(subgeo.id).fill}
								isActive={highlightedSubgeoId === subgeo.id}
								key={subgeo.id}
								onClick={() => handleSubgeoClick(subgeo.id)}
								subgeo={subgeo}
							/>
						)}
						overscan={100}
						totalCount={filteredSubgeos.length}
					/>
				</div>
			)}
		</div>
	)
}
